import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { googleApi, metaApi } from 'api';
import analyticsApi from 'api/analytics';
import chatsApi from 'api/chat';
import { Download } from 'lucide-react';
import { useAuth } from 'components/context/AuthContext';
import { useAnalyticsFilter } from 'context/AnalyticsFilterContext';
import AnalyticsStickyFilterBar from 'components/analytics/AnalyticsStickyFilterBar';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
// Recharts components removed as they are not used in the optimized version

// Componentes de logos oficiales de las plataformas
const GoogleAdsLogo = () => (
  <div className="flex items-center gap-2">
    <img 
      src="/google-ads-icon.png" 
      alt="Google Ads" 
      className="w-6 h-6"
    />
    <span className="text-sm font-medium text-white">Google Ads</span>
  </div>
);

const MetaAdsLogo = () => (
  <div className="flex items-center gap-2">
    <img 
      src="/meta-ads-icon.png" 
      alt="Meta Ads" 
      className="w-6 h-6"
    />
    <span className="text-sm font-medium text-white">Meta Ads</span>
  </div>
);

// (MetricComparisonCard and CampaignsWithImpressionsTable removed - dead code)

// Estilos para el efecto Liquid Glass
const liquidGlassStyles = `
  .liquid-glass {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 6px 25px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
  }
  .liquid-glass:hover {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -1px 0 rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
  }
  .liquid-glass::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
    transition: opacity 0.3s ease;
  }
  .liquid-glass:hover::before {
    opacity: 1.2;
  }
  .liquid-glass::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    border-radius: inherit;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent 50%, rgba(0, 0, 0, 0.05));
    pointer-events: none;
    transition: opacity 0.3s ease;
  }
  .liquid-glass:hover::after {
    opacity: 1.3;
  }
  .investment-card {
    position: relative;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(40px);
    -webkit-backdrop-filter: blur(40px);
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 0 0 1px rgba(255, 255, 255, 0.05);
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1), animation 0.6s ease-out;
    cursor: pointer;
    overflow: hidden;
  }
  
  .investment-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 50%,
      rgba(0, 0, 0, 0.05) 75%,
      rgba(0, 0, 0, 0.1) 100%
    );
    opacity: 0.8;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  .investment-card::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.8) 20%,
      rgba(255, 255, 255, 0.9) 50%,
      rgba(255, 255, 255, 0.8) 80%,
      transparent 100%
    );
    opacity: 0.6;
    transition: opacity 0.4s ease;
  }
  
  .investment-card:hover {
    transform: translateY(-4px) scale(1.02);
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(50px);
    -webkit-backdrop-filter: blur(50px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 
      0 20px 60px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.08),
      0 0 40px rgba(15, 49, 247, 0.1);
  }
  
  .investment-card:hover::before {
    opacity: 1;
  }
  
  .investment-card:hover::after {
    opacity: 1;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(15, 49, 247, 0.3) 20%,
      rgba(15, 49, 247, 0.5) 50%,
      rgba(15, 49, 247, 0.3) 80%,
      transparent 100%
    );
  }
  
  /* Efecto de refracción sutil */
  .investment-card .glass-refraction {
    position: absolute;
    top: 20%;
    left: 10%;
    width: 60%;
    height: 60%;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 30%,
      transparent 70%
    );
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.6s ease;
    pointer-events: none;
  }
  
  .investment-card:hover .glass-refraction {
    opacity: 1;
  }
  
  /* Estilos para dropdowns con Liquid Glass */
  .glass-dropdown {
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(40px);
    -webkit-backdrop-filter: blur(40px);
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1);
    transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  }
  
  .glass-dropdown:hover {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      0 0 20px rgba(15, 49, 247, 0.1);
  }
  
  .glass-dropdown-menu {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 20px 60px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }
  
  /* Tooltip del gráfico con Liquid Glass */
  .glass-tooltip {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }
  
  /* Animación de entrada para las tarjetas */
  .investment-card {
    animation: cardEntrance 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  }
  
  .investment-card:nth-child(1) { animation-delay: 0.1s; }
  .investment-card:nth-child(2) { animation-delay: 0.2s; }
  .investment-card:nth-child(3) { animation-delay: 0.3s; }
  
  /* Animación de entrada para componentes Liquid Glass */
  .liquid-glass {
    animation: glassEntrance 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  }
  
  .liquid-glass:nth-of-type(1) { animation-delay: 0.2s; }
  .liquid-glass:nth-of-type(2) { animation-delay: 0.4s; }
  .liquid-glass:nth-of-type(3) { animation-delay: 0.6s; }
  .liquid-glass:nth-of-type(4) { animation-delay: 0.8s; }
  .liquid-glass:nth-of-type(5) { animation-delay: 1.0s; }
  
  @keyframes cardEntrance {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes glassEntrance {
    from {
      opacity: 0;
      transform: translateY(40px) scale(0.9);
      backdrop-filter: blur(0px);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
      backdrop-filter: blur(20px);
    }
  }
  
  /* Efecto de respiración muy sutil en hover */
  .investment-card:hover {
    animation: gentleBreath 3s ease-in-out infinite;
  }
  
  /* Transición suave al salir del hover */
  .investment-card:not(:hover) {
    animation: none;
  }
  
  @keyframes gentleBreath {
    0%, 100% { 
      box-shadow: 
        0 20px 60px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -1px 0 rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(255, 255, 255, 0.08),
        0 0 40px rgba(15, 49, 247, 0.1);
    }
    50% { 
      box-shadow: 
        0 22px 65px rgba(0, 0, 0, 0.16),
        inset 0 1px 0 rgba(255, 255, 255, 0.52),
        inset 0 -1px 0 rgba(0, 0, 0, 0.16),
        0 0 0 1px rgba(255, 255, 255, 0.09),
        0 0 42px rgba(15, 49, 247, 0.12);
    }
  }
`;

// Componente Tabla de Comparación Semanal - OPTIMIZED
const WeeklyComparisonTable = ({ displayCurrency, weeklyComparisonData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (weeklyComparisonData) {
      setLoading(false);
      setError(null);
    }
  }, [weeklyComparisonData]);

  // Extraer datos de los props
  const currentWeekData = weeklyComparisonData?.currentData || null;
  const previousWeekData = weeklyComparisonData?.previousData || null;

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const formatChange = (change) => {
    if (change === null) return '-';
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  const getChangeColor = (change, invertLogic = false) => {
    if (change === null) return 'text-white/60';
    const isPositive = change > 0;
    const shouldBeGreen = invertLogic ? !isPositive : isPositive;
    return shouldBeGreen ? 'text-green-400' : 'text-red-400';
  };

  if (loading) {
    return (
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-6 shadow-lg mt-8">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto"></div>
          <p className="text-white/80 mt-2">Cargando comparación semanal...</p>
        </div>
      </div>
    );
  }

  if (error || !currentWeekData || !previousWeekData) {
    return null;
  }

  const current = currentWeekData.combined || {};
  const previous = previousWeekData.combined || {};

  const metrics = [
    {
      label: 'Gasto Total',
      current: current.investment || current.cost || 0,
      previous: previous.investment || previous.cost || 0,
      format: 'currency',
      invertLogic: false
    },
    {
      label: 'Conversiones',
      current: current.conversions || 0,
      previous: previous.conversions || 0,
      format: 'number',
      invertLogic: false
    },
    {
      label: 'CPA',
      current: (current.conversions > 0 ? (current.investment || current.cost || 0) / current.conversions : 0),
      previous: (previous.conversions > 0 ? (previous.investment || previous.cost || 0) / previous.conversions : 0),
      format: 'currency',
      invertLogic: true
    },
    {
      label: 'Clicks',
      current: current.clicks || 0,
      previous: previous.clicks || 0,
      format: 'number',
      invertLogic: false
    },
    {
      label: 'CPC',
      current: (current.clicks > 0 ? (current.investment || current.cost || 0) / current.clicks : 0),
      previous: (previous.clicks > 0 ? (previous.investment || previous.cost || 0) / previous.clicks : 0),
      format: 'currency',
      invertLogic: true
    },
    {
      label: 'Impresiones',
      current: current.impressions || 0,
      previous: previous.impressions || 0,
      format: 'number',
      invertLogic: false
    },
    {
      label: 'CTR',
      current: (current.impressions > 0 ? (current.clicks / current.impressions) * 100 : 0),
      previous: (previous.impressions > 0 ? (previous.clicks / previous.impressions) * 100 : 0),
      format: 'percentage',
      invertLogic: false
    }
  ];

  const formatValue = (value, format) => {
    if (format === 'currency') {
      return new Intl.NumberFormat('es-UY', {
        style: 'currency',
        currency: displayCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    if (format === 'percentage') {
      return `${value.toFixed(2)}%`;
    }
    return value.toLocaleString('es-UY');
  };

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 hover:scale-[1.02] transition-all duration-500 ease-out mt-8 sm:mt-12">
      <div className="relative z-10">
        <div className="flex items-center justify-center mb-6">
          <div className="text-center">
            <h3 className="text-lg font-bold text-white mb-1">Comparación Semanal</h3>
            <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
          </div>
        </div>

        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/30">
                <th className="text-left py-3 px-4 font-medium text-white">Métrica</th>
                <th className="text-center py-3 px-4 font-medium text-white">Esta Semana</th>
                <th className="text-center py-3 px-4 font-medium text-white">Semana Pasada</th>
                <th className="text-center py-3 px-4 font-medium text-white">Cambio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/20">
              {metrics.map((metric, index) => {
                const change = calculateChange(metric.current, metric.previous);
                return (
                  <tr key={index} className="hover:bg-white/10 transition-colors">
                    <td className="py-3 px-4 font-medium text-white">{metric.label}</td>
                    <td className="py-3 px-4 text-center text-white/90">
                      {formatValue(metric.current, metric.format)}
                    </td>
                    <td className="py-3 px-4 text-center text-white/70">
                      {formatValue(metric.previous, metric.format)}
                    </td>
                    <td className={`py-3 px-4 text-center font-semibold ${getChangeColor(change, metric.invertLogic)}`}>
                      <div className="flex items-center justify-center gap-1">
                        {change !== null && (
                          <>
                            {change > 0 ? '↑' : change < 0 ? '↓' : '→'}
                            <span>{formatChange(change)}</span>
                          </>
                        )}
                        {change === null && '-'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Componente ProjectionBar para mostrar barras de proyección
const ProjectionBar = ({ current, googleProjected, metaProjected, totalProjected, type }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  const googlePercentage = totalProjected > 0 ? (googleProjected / totalProjected) * 100 : 0;
  const metaPercentage = totalProjected > 0 ? (metaProjected / totalProjected) * 100 : 0;
  
  return (
    <div className="space-y-2">
      <div className="text-center">
        <span className="font-semibold text-lg text-blue-300">
          {type === 'cost' ? formatCurrency(totalProjected) : formatNumber(totalProjected)}
        </span>
        <div className="text-xs text-white/70 mt-1">Proyectado</div>
      </div>
      
      <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden relative group cursor-pointer">
        <div className="flex h-full">
          <div 
            className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-200" 
            style={{ width: `${googlePercentage}%` }}
            title={`Google: ${type === 'cost' ? formatCurrency(googleProjected) : formatNumber(googleProjected)}`}
          ></div>
          <div 
            className="bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-200" 
            style={{ width: `${metaPercentage}%` }}
            title={`Meta: ${type === 'cost' ? formatCurrency(metaProjected) : formatNumber(metaProjected)}`}
          ></div>
        </div>
        
        {/* Tooltip en hover */}
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
          <div>Google: {type === 'cost' ? formatCurrency(googleProjected) : formatNumber(googleProjected)}</div>
          <div>Meta: {type === 'cost' ? formatCurrency(metaProjected) : formatNumber(metaProjected)}</div>
        </div>
      </div>
      
      <div className="text-center">
        <span className="text-sm text-white/70">
          Actual: {type === 'cost' ? formatCurrency(current) : formatNumber(current)}
        </span>
      </div>
    </div>
  );
};

// Componente Monthly Projections Module
const MonthlyProjectionsModule = ({ monthlyProjectionsData }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (monthlyProjectionsData) {
      setLoading(false);
    }
  }, [monthlyProjectionsData]);

  // Calcular proyecciones usando los datos recibidos via props
  const projectionsData = useMemo(() => {
    if (!monthlyProjectionsData) return null;

    const {
      googleResponse,
      metaResponse,
      currentMonthCombinedResponse,
      daysInMonth,
      daysPassed,
      daysRemaining
    } = monthlyProjectionsData;

    // Procesar datos de Google (últimos 7 días)
    let googleData = null;
    if (googleResponse) {
      const googleApiData = googleResponse.data;
      googleData = {
        cost: googleApiData?.cost || 0,
        clicks: googleApiData?.clicks || 0,
        conversions: googleApiData?.conversions || 0,
        impressions: googleApiData?.impressions || 0
      };
    }
    
    // Procesar datos de Meta (últimos 7 días)
    let metaData = null;
    if (metaResponse) {
      const metaApiData = metaResponse.data;
      metaData = {
        cost: metaApiData?.total_cost || 0,
        clicks: metaApiData?.clicks || 0,
        conversions: metaApiData?.conversions || 0,
        impressions: metaApiData?.impressions || 0
      };
    }
    
    // Usar los datos del combined analytics (que ya maneja la conversión de monedas)
    const currentMonthData = {
      cost: currentMonthCombinedResponse?.combined?.investment || currentMonthCombinedResponse?.combined?.cost || 0,
      clicks: currentMonthCombinedResponse?.combined?.clicks || 0,
      conversions: currentMonthCombinedResponse?.combined?.conversions || 0,
      impressions: currentMonthCombinedResponse?.combined?.impressions || 0
    };
    
    const currentGoogleData = {
      cost: currentMonthCombinedResponse?.google_normalized?.investment || currentMonthCombinedResponse?.google_normalized?.cost || 0,
      clicks: currentMonthCombinedResponse?.google_normalized?.clicks || 0,
      conversions: currentMonthCombinedResponse?.google_normalized?.conversions || 0,
      impressions: currentMonthCombinedResponse?.google_normalized?.impressions || 0
    };
    
    const currentMetaData = {
      cost: currentMonthCombinedResponse?.meta_normalized?.investment || currentMonthCombinedResponse?.meta_normalized?.cost || 0,
      clicks: currentMonthCombinedResponse?.meta_normalized?.clicks || 0,
      conversions: currentMonthCombinedResponse?.meta_normalized?.conversions || 0,
      impressions: currentMonthCombinedResponse?.meta_normalized?.impressions || 0
    };
    
    // Calcular proyecciones
    return {
      // Proyecciones del mes completo
      monthly: {
        total: {
          cost: currentMonthData.cost * (daysInMonth / daysPassed),
          clicks: currentMonthData.clicks * (daysInMonth / daysPassed),
          conversions: currentMonthData.conversions * (daysInMonth / daysPassed),
          impressions: currentMonthData.impressions * (daysInMonth / daysPassed)
        },
        google: {
          cost: currentGoogleData.cost * (daysInMonth / daysPassed),
          clicks: currentGoogleData.clicks * (daysInMonth / daysPassed),
          conversions: currentGoogleData.conversions * (daysInMonth / daysPassed),
          impressions: currentGoogleData.impressions * (daysInMonth / daysPassed)
        },
        meta: {
          cost: currentMetaData.cost * (daysInMonth / daysPassed),
          clicks: currentMetaData.clicks * (daysInMonth / daysPassed),
          conversions: currentMetaData.conversions * (daysInMonth / daysPassed),
          impressions: currentMetaData.impressions * (daysInMonth / daysPassed)
        }
      },
      // Proyecciones basadas en promedio de últimos 7 días
      basedOnLast7Days: {
        google: googleData ? {
          cost: googleData.cost * (daysInMonth / 7),
          clicks: googleData.clicks * (daysInMonth / 7),
          conversions: googleData.conversions * (daysInMonth / 7),
          impressions: googleData.impressions * (daysInMonth / 7)
        } : null,
        meta: metaData ? {
          cost: metaData.cost * (daysInMonth / 7),
          clicks: metaData.clicks * (daysInMonth / 7),
          conversions: metaData.conversions * (daysInMonth / 7),
          impressions: metaData.impressions * (daysInMonth / 7)
        } : null
      },
      // Datos actuales del mes
      current: {
        total: currentMonthData,
        google: currentGoogleData,
        meta: currentMetaData
      },
      // Información del mes
      monthInfo: {
        daysInMonth,
        daysPassed,
        daysRemaining
      }
    };
  }, [monthlyProjectionsData]);

  // Código anterior eliminado - ahora usa datos via props

  if (loading || !projectionsData) {
    return (
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg">
        <div className="text-center text-white">Cargando proyecciones...</div>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 hover:scale-[1.02] transition-all duration-500 ease-out">
      <div className="relative z-10">
        <h3 className="text-lg font-bold text-white mb-1">Proyecciones Mensuales</h3>
        <div className="w-16 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto mb-6"></div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Cost */}
          <div className="space-y-3">
            <h4 className="font-semibold text-white text-center">Costo</h4>
            <ProjectionBar 
              current={projectionsData.current.total.cost}
              googleProjected={projectionsData.monthly.google.cost}
              metaProjected={projectionsData.monthly.meta.cost}
              totalProjected={projectionsData.monthly.total.cost}
              type="cost"
            />
          </div>
          
          {/* Clicks */}
          <div className="space-y-3">
            <h4 className="font-semibold text-white text-center">Clicks</h4>
            <ProjectionBar 
              current={projectionsData.current.total.clicks}
              googleProjected={projectionsData.monthly.google.clicks}
              metaProjected={projectionsData.monthly.meta.clicks}
              totalProjected={projectionsData.monthly.total.clicks}
              type="clicks"
            />
          </div>
          
          {/* Conversions */}
          <div className="space-y-3">
            <h4 className="font-semibold text-white text-center">Conversiones</h4>
            <ProjectionBar 
              current={projectionsData.current.total.conversions}
              googleProjected={projectionsData.monthly.google.conversions}
              metaProjected={projectionsData.monthly.meta.conversions}
              totalProjected={projectionsData.monthly.total.conversions}
              type="conversions"
            />
          </div>
          
          {/* Impressions */}
          <div className="space-y-3">
            <h4 className="font-semibold text-white text-center">Impresiones</h4>
            <ProjectionBar 
              current={projectionsData.current.total.impressions}
              googleProjected={projectionsData.monthly.google.impressions}
              metaProjected={projectionsData.monthly.meta.impressions}
              totalProjected={projectionsData.monthly.total.impressions}
              type="impressions"
            />
          </div>
        </div>
        
        <div className="mt-4 text-center text-sm text-gray-500">
          Proyección basada en el gasto actual del mes ({projectionsData.monthInfo.daysRemaining} días restantes)
        </div>
      </div>
    </div>
  );
};
const ReporteSemanal = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7D');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dateValidationError, setDateValidationError] = useState('');
  const [combinedData, setCombinedData] = useState(null);
  
  // Estados para datos de comparación
  const [comparisonData, setComparisonData] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const { auth } = useAuth();
  const { active_brand_id } = auth?.user || {};
  const { startDate: filterStartDate, endDate: filterEndDate, platform: filterPlatform, selectedCampaignIds: filterCampaignIds } = useAnalyticsFilter();
  // Referencias para el contenedor que se exportará a PDF y tablas específicas
  const dashboardRef = useRef(null);
  const weeklyComparisonRef = useRef(null);
  const platformMetricsRef = useRef(null);

  const basePeriods = useMemo(() => [
    { key: '7D', label: 'Últimos 7 días', days: 7, granularity: 'daily' },
    { key: '21D', label: 'Últimos 21 días', days: 21, granularity: 'daily' }, // Límite máximo de Meta
    { key: '30D', label: 'Últimos 30 días', days: 30, granularity: 'weekly' },
    { key: '90D', label: 'Últimos 90 días', days: 90, granularity: 'weekly' },
    { key: 'ESTE_MES', label: 'Este mes', days: 'current_month', granularity: 'daily' },
    { key: 'MES_PASADO', label: 'El mes pasado', days: 'last_month', granularity: 'daily' },
    { key: 'ESTE_AÑO', label: 'Este año', days: 'current_year', granularity: 'weekly' }
  ], []);

  const getPeriodDisplayText = () => {
    if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      return `${format(startDate, 'dd/MM')} - ${format(endDate, 'dd/MM')}`;
    }
    const period = basePeriods.find(p => p.key === selectedPeriod);
    return period ? period.label : 'Seleccionar período';
  };

  // Agregar período personalizado si existe
  const periods = useMemo(() => {
    if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      const label = `${format(startDate, 'dd/MM')} - ${format(endDate, 'dd/MM')}`;
      const diffTime = Math.abs(endDate - startDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return [...basePeriods, { key: 'CUSTOM', label, days, granularity: 'daily' }];
    }
    return basePeriods;
  }, [selectedPeriod, customStartDate, customEndDate, basePeriods]);

  // Verificar conexiones
  const isGoogleConnected = auth?.user?.google_ads_connected && auth?.user?.google_ads_customer_id;
  const isMetaConnected = auth?.user?.meta_connected && auth?.user?.meta_ad_account_id;
  const hasAnyConnection = isGoogleConnected || isMetaConnected;

  // Función optimizada para obtener datos de comparación
  const fetchComparisonData = useCallback(async (periodKey, days) => {
    try {
      const comparisonPeriod = calculateComparisonPeriod(periodKey, days, customStartDate, customEndDate);
      if (!comparisonPeriod) return null;
      
      const startDate = format(comparisonPeriod.start, 'yyyy-MM-dd');
      const endDate = format(comparisonPeriod.end, 'yyyy-MM-dd');
      
      const response = await analyticsApi.fetchCombinedAnalyticsSummary(
        startDate,
        endDate,
        active_brand_id
      );
      return response;
      
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      return null;
    }
  }, [active_brand_id, customStartDate, customEndDate]);

  // Estados para datos compartidos con sub-componentes
  const [weeklyComparisonData, setWeeklyComparisonData] = useState(null);
  const [monthlyProjectionsData, setMonthlyProjectionsData] = useState(null);

  // Función para obtener datos de comparación semanal
  const fetchWeeklyComparisonData = useCallback(async () => {
    try {
      const today = new Date();
      
      // Semana actual: últimos 7 días completos
      const currentWeekEnd = subDays(today, 1);
      const currentWeekStart = subDays(currentWeekEnd, 6);
      
      // Semana anterior: 7 días antes de la semana actual
      const previousWeekEnd = subDays(currentWeekStart, 1);
      const previousWeekStart = subDays(previousWeekEnd, 6);

      // Fetch datos de ambas semanas
      const [currentData, previousData] = await Promise.all([
        analyticsApi.fetchCombinedAnalyticsSummary(
          format(currentWeekStart, 'yyyy-MM-dd'),
          format(currentWeekEnd, 'yyyy-MM-dd'),
          active_brand_id
        ),
        analyticsApi.fetchCombinedAnalyticsSummary(
          format(previousWeekStart, 'yyyy-MM-dd'),
          format(previousWeekEnd, 'yyyy-MM-dd'),
          active_brand_id
        )
      ]);

      return { currentData, previousData };
    } catch (error) {
      console.error('Error fetching weekly comparison data:', error);
      return null;
    }
  }, [active_brand_id]);

  // Función para obtener datos de proyecciones mensuales
  const fetchMonthlyProjectionsData = useCallback(async () => {
    try {
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      // Fechas del mes actual
      const startOfMonth = new Date(currentYear, currentMonth, 1);
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
      const daysInMonth = endOfMonth.getDate();
      const daysPassed = today.getDate();
      const daysRemaining = daysInMonth - daysPassed;
      
      // Obtener datos de "Este mes" para el estado actual
      const currentMonthStartStr = format(startOfMonth, 'yyyy-MM-dd');
      const currentMonthEndStr = format(today, 'yyyy-MM-dd');
      
      // Obtener datos de los últimos 7 días para calcular promedios
      const sevenDaysAgo = subDays(today, 7);
      const endDate = subDays(today, 1);
      const startDateStr = format(sevenDaysAgo, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      
      // Obtener datos de los últimos 7 días para calcular promedios
      const [googleResponse, metaResponse] = await Promise.allSettled([
        isGoogleConnected ? googleApi.fetchGoogleAnalyticsData(startDateStr, endDateStr) : Promise.resolve(null),
        isMetaConnected ? metaApi.fetchMetaAnalyticsData(startDateStr, endDateStr) : Promise.resolve(null)
      ]);
      
      // Obtener datos del mes actual usando combined analytics
      const currentMonthCombinedResponse = await analyticsApi.fetchCombinedAnalyticsSummary(
        currentMonthStartStr,
        currentMonthEndStr,
        active_brand_id
      );
      
      return {
        googleResponse: googleResponse.status === 'fulfilled' ? googleResponse.value : null,
        metaResponse: metaResponse.status === 'fulfilled' ? metaResponse.value : null,
        currentMonthCombinedResponse,
        daysInMonth,
        daysPassed,
        daysRemaining
      };
    } catch (error) {
      console.error('Error fetching monthly projections data:', error);
      return null;
    }
  }, [active_brand_id, isGoogleConnected, isMetaConnected]);

  // FUNCIÓN CONSOLIDADA: Fetch todos los datos necesarios (usa contexto de filtros: fechas, plataforma, campañas)
  const fetchAllAnalyticsData = useCallback(async (periodKey, days) => {
    if (!hasAnyConnection || !filterStartDate || !filterEndDate) return;

    const campaignIdsParam = filterCampaignIds?.length ? filterCampaignIds : null;

    try {
      // 1. Datos principales del período actual - fechas y filtros desde contexto
      const mainDataPromise = analyticsApi.fetchCombinedAnalyticsSummary(
        filterStartDate,
        filterEndDate,
        active_brand_id,
        'total',
        'campaign',
        filterPlatform,
        campaignIdsParam
      );

      // 2. Datos de comparación (período anterior)
      const comparisonDataPromise = fetchComparisonData(periodKey, days);

      // 3. Datos diarios de Google (si está conectado)
      const googleDailyPromise = isGoogleConnected ?
        googleApi.fetchGoogleAnalyticsData(filterStartDate, filterEndDate) :
        Promise.resolve(null);

      // 4. Datos diarios de Meta (si está conectado)
      const metaDailyPromise = isMetaConnected ?
        metaApi.fetchMetaAnalyticsData(filterStartDate, filterEndDate) :
        Promise.resolve(null);
      
      // 5. Datos de comparación semanal (WeeklyComparisonTable)
      const weeklyComparisonPromise = fetchWeeklyComparisonData();
      
      // 6. Datos de proyecciones mensuales (MonthlyProjectionsModule)
      const monthlyProjectionsPromise = fetchMonthlyProjectionsData();
      
      // Ejecutar todas las requests en paralelo
      const [
        mainData,
        comparisonData,
        ,
        ,
        weeklyComparisonData,
        monthlyProjectionsData
      ] = await Promise.allSettled([
        mainDataPromise,
        comparisonDataPromise,
        googleDailyPromise,
        metaDailyPromise,
        weeklyComparisonPromise,
        monthlyProjectionsPromise
      ]);
      
      // Procesar resultados
      if (mainData.status === 'fulfilled' && mainData.value) {
        setCombinedData(mainData.value);
        setDisplayCurrency(mainData.value.display_currency || 'USD');
      }
      
      if (comparisonData.status === 'fulfilled' && comparisonData.value) {
        setComparisonData(comparisonData.value);
      }
      
      // Almacenar datos para sub-componentes
      setWeeklyComparisonData(weeklyComparisonData.status === 'fulfilled' ? weeklyComparisonData.value : null);
      setMonthlyProjectionsData(monthlyProjectionsData.status === 'fulfilled' ? monthlyProjectionsData.value : null);
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  }, [hasAnyConnection, isGoogleConnected, isMetaConnected, active_brand_id, fetchComparisonData, fetchWeeklyComparisonData, fetchMonthlyProjectionsData, filterStartDate, filterEndDate, filterPlatform, filterCampaignIds]);

  // Fetch cuando cambian los filtros del contexto (período, plataforma, campañas)
  useEffect(() => {
    const period = periods.find(p => p.key === selectedPeriod);
    if (period) {
      fetchAllAnalyticsData(selectedPeriod, period.days);
    }
  }, [filterStartDate, filterEndDate, filterPlatform, filterCampaignIds, fetchAllAnalyticsData, periods, selectedPeriod]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPeriodDropdownOpen && !event.target.closest('.period-dropdown-container')) {
        setIsPeriodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPeriodDropdownOpen]);

  // useEffect para manejar el click outside del modal de fechas personalizadas
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCustomDateModalOpen && !event.target.closest('.custom-date-modal')) {
        setIsCustomDateModalOpen(false);
        setDateValidationError('');
        setCustomStartDate('');
        setCustomEndDate('');
      }
    };

    if (isCustomDateModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCustomDateModalOpen]);

  // Función para calcular el cambio porcentual
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Función para formatear el cambio porcentual
  const formatPercentageChange = (change) => {
    if (change === null) return null;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  // Función para descargar el dashboard como PDF con resumen ejecutivo
  const downloadPDF = async () => {
    if (!dashboardRef.current) return;
    
    setIsDownloadingPDF(true);
    
    try {
      // Esperar un momento para que el dashboard se renderice completamente
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Crear PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      
      // === PÁGINA 1: RESUMEN EJECUTIVO ===
      
      // Fondo degradado (simulado con rectángulos)
      pdf.setFillColor(15, 23, 42); // slate-900
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Header con logo/título
      pdf.setFontSize(24);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Reporte de Analytics', margin, 30);
      
      // Línea decorativa
      pdf.setDrawColor(59, 130, 246); // blue-500
      pdf.setLineWidth(2);
      pdf.line(margin, 35, margin + 60, 35);
      
      // Período y fecha
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(203, 213, 225); // slate-300
      pdf.text(`Período: ${getPeriodDisplayText()}`, margin, 45);
      pdf.text(`Fecha de generación: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, margin, 52);
      
      // Generar resumen ejecutivo con IA usando captura completa del reporte
      let aiSummary = 'Generando resumen ejecutivo...';
      try {
        // Capturar screenshot completo del reporte
        const fullReportScreenshot = {
          fullReport: null,
          period: getPeriodDisplayText(),
          currency: displayCurrency
        };
        
        // Capturar todo el dashboard completo
        if (dashboardRef.current) {
          const fullCanvas = await html2canvas(dashboardRef.current, {
            scale: 0.8, // Reducir escala para optimizar
            useCORS: true,
            backgroundColor: '#0f172a', // slate-900
            height: dashboardRef.current.scrollHeight,
            width: dashboardRef.current.scrollWidth
          });
          fullReportScreenshot.fullReport = fullCanvas.toDataURL('image/jpeg', 0.7);
        }
        
        const summaryResponse = await chatsApi.generateAnalyticsSummary(fullReportScreenshot);
        
        if (summaryResponse && summaryResponse.data) {
          aiSummary = summaryResponse.data.summary || 'No se pudo generar el resumen ejecutivo.';
        } else if (summaryResponse && summaryResponse.summary) {
          aiSummary = summaryResponse.summary;
        } else {
          aiSummary = 'No se pudo generar el resumen ejecutivo.';
        }
        
      } catch (error) {
        console.error('Error al generar resumen ejecutivo:', error);
        aiSummary = 'No se pudo generar el resumen ejecutivo en este momento.';
      }
      
      // Título del resumen
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(147, 197, 253); // blue-300
      pdf.text('Insights Clave', margin, 70);
      
      // Contenido del resumen (con saltos de línea automáticos)
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(226, 232, 240); // slate-200
      
      // Dividir el texto en líneas que quepan en el ancho de página
      const splitSummary = pdf.splitTextToSize(aiSummary, contentWidth);
      let yPosition = 82;
      const lineHeight = 7;
      
      splitSummary.forEach((line) => {
        // Si llegamos al final de la página, crear nueva página
        if (yPosition > pageHeight - margin - 20) {
          pdf.addPage();
          
          // Repetir fondo en nueva página
          pdf.setFillColor(15, 23, 42);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          
          yPosition = margin;
        }
        
        pdf.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
      
      // Métricas clave en recuadros
      yPosition += 10;
      if (combinedData?.combined) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(147, 197, 253); // blue-300
        pdf.text('Métricas Clave', margin, yPosition);
        yPosition += 10;
        
        const metrics = [
          { label: 'Gasto Total', value: `${displayCurrency} ${(combinedData.combined.investment || combinedData.combined.cost || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          { label: 'Conversiones', value: (combinedData.combined.conversions || 0).toLocaleString('es-UY') },
          { label: 'CPA', value: `${displayCurrency} ${((combinedData.combined.conversions > 0 ? (combinedData.combined.investment || combinedData.combined.cost || 0) / combinedData.combined.conversions : 0)).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          { label: 'Clicks', value: (combinedData.combined.clicks || 0).toLocaleString('es-UY') },
        ];
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        metrics.forEach((metric, index) => {
          const xPos = margin + (index % 2) * (contentWidth / 2);
          const yPos = yPosition + Math.floor(index / 2) * 15;
          
          // Recuadro
          pdf.setFillColor(51, 65, 85); // slate-700
          pdf.roundedRect(xPos, yPos - 5, (contentWidth / 2) - 5, 12, 2, 2, 'F');
          
          // Texto
          pdf.setTextColor(203, 213, 225); // slate-300
          pdf.text(metric.label + ':', xPos + 3, yPos);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(147, 197, 253); // blue-300
          pdf.text(metric.value, xPos + 3, yPos + 5);
          pdf.setFont('helvetica', 'normal');
        });
      }
      
      // === PÁGINAS SIGUIENTES: CAPTURA DEL DASHBOARD ===
      pdf.addPage();
      
      // Configurar opciones para html2canvas con mejor compresión
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 1, // Reducir escala para menor tamaño
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0f172a', // Color de fondo del dashboard
        logging: false,
        windowWidth: dashboardRef.current.scrollWidth,
        windowHeight: dashboardRef.current.scrollHeight
      });
      
      // Obtener dimensiones del canvas
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Convertir canvas a imagen JPEG con compresión (más liviano que PNG)
      const imgData = canvas.toDataURL('image/jpeg', 0.7); // 0.7 = 70% de calidad
      
      // Agregar primera página de captura
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Agregar páginas adicionales si es necesario
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Descargar PDF
      const fileName = `analytics_${getPeriodDisplayText()}_${format(new Date(), 'dd-MM-yyyy')}.pdf`;
      pdf.save(fileName);
      
      // Enviar PDF por email
      setIsSendingEmail(true);
      try {
        // Convertir el PDF a base64 para enviarlo por email
        const pdfBase64 = pdf.output('dataurlstring').split(',')[1];
        
        // Verificar tamaño del PDF (límite de Resend: 40MB)
        const pdfSizeInBytes = (pdfBase64.length * 3) / 4; // Calcular tamaño aproximado
        const pdfSizeInMB = pdfSizeInBytes / (1024 * 1024);
        
        
        if (pdfSizeInMB > 35) {
          console.warn('⚠️ PDF demasiado grande para enviar por email');
          alert('✅ PDF descargado exitosamente!\n⚠️ El archivo es muy grande para enviarlo por email. Te lo descargamos en tu dispositivo.');
          setIsSendingEmail(false);
          return;
        }
        
        const reportData = {
          email: 'dipasquaprats@gmail.com',
          fileName: fileName,
          period: getPeriodDisplayText(),
          userName: auth?.user?.name || auth?.user?.email || 'Usuario',
          generatedDate: format(new Date(), 'dd/MM/yyyy HH:mm'),
          summary: aiSummary, // Incluir el resumen generado por IA
          combined: combinedData?.combined || {}, // Incluir métricas principales
          currency: displayCurrency
        };
        
        await chatsApi.sendAnalyticsReport(pdfBase64, reportData);
        
        // Mostrar notificación de éxito
        alert('✅ PDF descargado y enviado a dipasquaprats@gmail.com exitosamente!');
      } catch (emailError) {
        console.error('Error al enviar email:', emailError);
        // No bloqueamos el proceso si falla el email, el PDF ya se descargó
        alert('⚠️ PDF descargado exitosamente, pero hubo un error al enviar el email.');
      } finally {
        setIsSendingEmail(false);
      }
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Hubo un error al generar el PDF. Por favor, intenta nuevamente.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Función para obtener el color del cambio
  const getChangeColor = (change, isInverted = false) => {
    if (change === null) return 'text-gray-500';
    
    const isPositive = change > 0;
    const shouldBeGreen = isInverted ? !isPositive : isPositive;
    
    return shouldBeGreen ? 'text-green-600' : 'text-red-600';
  };

  // Función para obtener las fechas del período de comparación
  const getComparisonDates = () => {
    if (!selectedPeriod || !comparisonData) return null;
    
    const period = periods.find(p => p.key === selectedPeriod);
    if (!period) return null;
    
    const comparisonPeriod = calculateComparisonPeriod(selectedPeriod, period.days);
    if (!comparisonPeriod) return null;
    
    return {
      start: format(comparisonPeriod.start, 'dd/MM/yyyy'),
      end: format(comparisonPeriod.end, 'dd/MM/yyyy')
    };
  };

  const exchangeRatesForDisplay = { USD: 1, ...(combinedData?.exchange_rates || {}) };
  const formatCurrency = (value, currency = 'USD') => {
    if (typeof value !== 'number') return '0';
    let convertedValue = value;
    let targetCurrency = displayCurrency || 'USD';
    if (currency !== targetCurrency && exchangeRatesForDisplay[currency] != null && exchangeRatesForDisplay[targetCurrency] != null) {
      convertedValue = value * (exchangeRatesForDisplay[currency] / exchangeRatesForDisplay[targetCurrency]);
    } else if (currency !== targetCurrency) {
      targetCurrency = currency;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: targetCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedValue);
  };

  // Función para validar fechas personalizadas
  const validateCustomDates = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return 'Ambas fechas son requeridas';
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    
    // No permitir fechas futuras
    if (start > today || end > today) {
      return 'No se pueden seleccionar fechas futuras';
    }
    
    // Validar que la fecha de inicio no sea posterior a la de fin
    if (start > end) {
      return 'La fecha de inicio no puede ser posterior a la fecha de fin';
    }
    
    // Validar que la fecha de fin no sea anterior a la de inicio
    if (end < start) {
      return 'La fecha de fin no puede ser anterior a la fecha de inicio';
    }
    
    // Validar rango máximo (ej: no más de 1 año)
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 365) {
      return 'El rango máximo permitido es de 365 días';
    }
    
    return null; // Sin errores
  };

  // Función para confirmar fechas personalizadas
  const confirmCustomDates = () => {
    const error = validateCustomDates(customStartDate, customEndDate);
    
    if (error) {
      setDateValidationError(error);
      return;
    }
    
    // Si no hay errores, aplicar las fechas y cerrar modal
    setDateValidationError('');
    setIsCustomDateModalOpen(false);
    
    // Cambiar a período personalizado - esto activará el useEffect automáticamente
    setSelectedPeriod('CUSTOM');
  };

  // Función para calcular el período de comparación
  const calculateComparisonPeriod = (periodKey, days, customStartDate = '', customEndDate = '') => {
    const today = new Date();
    let comparisonStart, comparisonEnd;
    
    switch (periodKey) {
      case '7D':
        // Usar la misma lógica que WeeklyComparisonTable
        const currentWeekEnd = subDays(today, 1);        // Ayer
        const currentWeekStart = subDays(currentWeekEnd, 6); // 6 días antes de ayer
        
        // Período anterior: 7 días antes de la semana actual
        comparisonEnd = subDays(currentWeekStart, 1);     // 1 día antes del inicio
        comparisonStart = subDays(comparisonEnd, 6);      // 6 días antes del final
        break;
        
      case '21D':
      case '30D':
      case '90D':
        // Para períodos de días, el período de comparación son los mismos días anteriores
        comparisonEnd = new Date(today);
        comparisonEnd.setDate(comparisonEnd.getDate() - days);
        comparisonStart = new Date(comparisonEnd);
        comparisonStart.setDate(comparisonStart.getDate() - days);
        break;
        
      case 'ESTE_MES':
        // Comparar con el mes pasado
        comparisonEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        comparisonStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        break;
        
      case 'MES_PASADO':
        // Comparar con el mes anterior al pasado
        comparisonEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
        comparisonStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        break;
        
      case 'ESTE_AÑO':
        // Comparar con el año pasado
        comparisonEnd = new Date(today.getFullYear() - 1, 11, 31);
        comparisonStart = new Date(today.getFullYear() - 1, 0, 1);
        break;
        
      case 'CUSTOM':
        // Para fechas personalizadas, calcular basado en la diferencia de días
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate);
          const endDate = new Date(customEndDate);
          const diffTime = Math.abs(endDate - startDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          comparisonEnd = new Date(startDate);
          comparisonEnd.setDate(comparisonEnd.getDate() - 1);
          comparisonStart = new Date(comparisonEnd);
          comparisonStart.setDate(comparisonStart.getDate() - diffDays);
        }
        break;
        
      default:
        return null;
    }
    
    return {
      start: comparisonStart,
      end: comparisonEnd
    };
  };



  // Obtener totales desde combined analytics (como en el inicio)
  const totalInvestment = combinedData?.combined?.investment || combinedData?.combined?.cost || 0;
  const totalGoogleInvestment = combinedData?.google_normalized?.investment || combinedData?.google_normalized?.cost || 0;
  const totalMetaInvestment = combinedData?.meta_normalized?.investment || combinedData?.meta_normalized?.cost || 0;

  // Calcular métricas automáticamente
  const totalConversions = combinedData?.combined?.conversions || 0;
  const totalClicks = combinedData?.combined?.clicks || 0;
  const totalImpressions = combinedData?.combined?.impressions || 0;
  
  // Métricas específicas de Google
  const totalGoogleConversions = combinedData?.google_normalized?.conversions || 0;
  const totalGoogleClicks = combinedData?.google_normalized?.clicks || 0;
  const totalGoogleImpressions = combinedData?.google_normalized?.impressions || 0;
  
  // Métricas específicas de Meta
  const totalMetaConversions = combinedData?.meta_normalized?.conversions || 0;
  const totalMetaClicks = combinedData?.meta_normalized?.clicks || 0;
  const totalMetaImpressions = combinedData?.meta_normalized?.impressions || 0;

  // Cálculos de métricas
  const calculatedCPA = totalConversions > 0 ? Number((totalInvestment / totalConversions).toFixed(2)) : 0;
  const calculatedCPC = totalClicks > 0 ? Number((totalInvestment / totalClicks).toFixed(2)) : 0;
  const calculatedCPM = totalImpressions > 0 ? Number((totalInvestment / (totalImpressions / 1000)).toFixed(2)) : 0;
  const calculatedCTR = totalImpressions > 0 ? Number(((totalClicks / totalImpressions) * 100).toFixed(2)) : 0;
  
  // Cálculos específicos de Google
  const calculatedGoogleCPA = totalGoogleConversions > 0 ? Number((totalGoogleInvestment / totalGoogleConversions).toFixed(2)) : 0;
  const calculatedGoogleCPC = totalGoogleClicks > 0 ? Number((totalGoogleInvestment / totalGoogleClicks).toFixed(2)) : 0;
  const calculatedGoogleCPM = totalGoogleImpressions > 0 ? Number((totalGoogleInvestment / (totalGoogleImpressions / 1000)).toFixed(2)) : 0;
  const calculatedGoogleCTR = totalGoogleImpressions > 0 ? Number(((totalGoogleClicks / totalGoogleImpressions) * 100).toFixed(2)) : 0;
  
  // Cálculos específicos de Meta
  const calculatedMetaCPA = totalMetaConversions > 0 ? Number((totalMetaInvestment / totalMetaConversions).toFixed(2)) : 0;
  const calculatedMetaCPC = totalMetaClicks > 0 ? Number((totalMetaInvestment / totalMetaClicks).toFixed(2)) : 0;
  const calculatedMetaCPM = totalMetaImpressions > 0 ? Number((totalMetaInvestment / (totalMetaImpressions / 1000)).toFixed(2)) : 0;
  const calculatedMetaCTR = totalMetaImpressions > 0 ? Number(((totalMetaClicks / totalMetaImpressions) * 100).toFixed(2)) : 0;

  return (
    <>
      <style>{liquidGlassStyles}</style>
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6" ref={dashboardRef}>

        {/* Barra de filtros sticky (período, plataforma, campañas) - compartida con Analytics20 */}
        <AnalyticsStickyFilterBar />

        {/* Header con Botón de Descarga */}
        <div className="flex justify-end items-center mb-8 gap-4">
          {/* Botón de Descarga PDF */}
          <button
            onClick={downloadPDF}
            disabled={isDownloadingPDF || isSendingEmail}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 active:scale-[0.98] flex items-center gap-2 whitespace-nowrap"
            title="Descargar reporte en PDF y enviar por email"
          >
            {isDownloadingPDF ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Generando...</span>
              </>
            ) : isSendingEmail ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Enviando email...</span>
              </>
            ) : (
              <>
                <Download size={18} />
                <span className="hidden sm:inline">Descargar y Enviar</span>
              </>
            )}
          </button>
        </div>

        {/* Tabla Comparativa Semanal - Primera sección */}
        <div className="mt-8 sm:mt-12" ref={weeklyComparisonRef}>
          <WeeklyComparisonTable 
            displayCurrency={displayCurrency} 
            weeklyComparisonData={weeklyComparisonData}
          />
        </div>

        {/* Platform Metrics Table - Mobile Optimized */}
        <div className="mt-16 sm:mt-20">
          <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 hover:scale-[1.02] transition-all duration-500 ease-out">
          <div className="relative z-10">
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/30">
                      <th className="text-left py-3 px-4 font-medium text-white">Plataforma</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Gasto Total</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Conversiones</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CPA</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Clicks</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CPC</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Impresiones</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CPM</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  <tr className="hover:bg-white/10 transition-colors">
                    <td className="py-4 px-4 font-medium text-white">
                      {auth?.user?.business_name || 'Mi Negocio'}
                    </td>
                    <td className="py-4 px-4 text-center font-semibold text-white">
                      <div>
                        {formatCurrency(totalInvestment, displayCurrency)}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalInvestment, comparisonData?.combined?.investment || comparisonData?.combined?.cost))}`}>
                            {formatPercentageChange(calculatePercentageChange(totalInvestment, comparisonData?.combined?.investment || comparisonData?.combined?.cost))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {totalConversions.toLocaleString()}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalConversions, comparisonData?.combined?.conversions))}`}>
                            {formatPercentageChange(calculatePercentageChange(totalConversions, comparisonData?.combined?.conversions))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {calculatedCPA > 0 ? 
                          formatCurrency(calculatedCPA, displayCurrency) : 
                          'N/A'
                        }
                        {comparisonData && calculatedCPA > 0 && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCPA, comparisonData?.combined?.conversions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.conversions : 0), true)}`}>
                            {formatPercentageChange(calculatePercentageChange(calculatedCPA, comparisonData?.combined?.conversions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.conversions : 0))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {totalClicks.toLocaleString()}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalClicks, comparisonData?.combined?.clicks))}`}>
                            {formatPercentageChange(calculatePercentageChange(totalClicks, comparisonData?.combined?.clicks))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {calculatedCPC > 0 ? 
                          formatCurrency(calculatedCPC, displayCurrency) : 
                          'N/A'
                        }
                        {comparisonData && calculatedCPC > 0 && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCPC, comparisonData?.combined?.clicks > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.clicks : 0), true)}`}>
                            {formatPercentageChange(calculatePercentageChange(calculatedCPC, comparisonData?.combined?.clicks > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.clicks : 0))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {totalImpressions.toLocaleString()}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalImpressions, comparisonData?.combined?.impressions))}`}>
                            {formatPercentageChange(calculatePercentageChange(totalImpressions, comparisonData?.combined?.impressions))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {calculatedCPM > 0 ? 
                          formatCurrency(calculatedCPM, displayCurrency) : 
                          'N/A'
                        }
                        {comparisonData && calculatedCPM > 0 && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCPM, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / (comparisonData?.combined?.impressions / 1000) : 0), true)}`}>
                            {formatPercentageChange(calculatePercentageChange(calculatedCPM, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / (comparisonData?.combined?.impressions / 1000) : 0))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {calculatedCTR > 0 ? 
                          `${calculatedCTR}%` : 
                          'N/A'
                        }
                        {comparisonData && calculatedCTR > 0 && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCTR, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.clicks / comparisonData?.combined?.impressions) * 100 : 0), false)}`}>
                            {formatPercentageChange(calculatePercentageChange(calculatedCTR, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.clicks / comparisonData?.combined?.impressions) * 100 : 0))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
          </div>
          
          {/* Leyenda de comparación */}
          {comparisonData && getComparisonDates() && (
            <div className="mt-4">
              <div className="text-sm text-gray-600 text-center">
                <span>📈 Comparando contra el período anterior: {getComparisonDates().start} - {getComparisonDates().end}</span>
              </div>
            </div>
          )}
        </div>
          </div>
        </div>

        {/* Monthly Projections Module */}
        <div className="mt-16 sm:mt-20">
        <MonthlyProjectionsModule monthlyProjectionsData={monthlyProjectionsData} />
                      </div>



        {/* Tabla de Campañas con Impresiones - Mobile Optimized */}
        
        {/* Platform Metrics Table - Google Ads y Meta Ads por separado */}
        <div className="mt-16 sm:mt-20" ref={platformMetricsRef}>
          <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 hover:scale-[1.02] transition-all duration-500 ease-out">
            <div className="relative z-10">
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/30">
                      <th className="text-left py-3 px-4 font-medium text-white">Plataforma</th>
                      <th className="text-center py-3 px-4 font-medium text-white">Gasto Total</th>
                      <th className="text-center py-3 px-4 font-medium text-white">Conversiones</th>
                      <th className="text-center py-3 px-4 font-medium text-white">CPA</th>
                      <th className="text-center py-3 px-4 font-medium text-white">Clicks</th>
                      <th className="text-center py-3 px-4 font-medium text-white">CPC</th>
                      <th className="text-center py-3 px-4 font-medium text-white">Impresiones</th>
                      <th className="text-center py-3 px-4 font-medium text-white">CPM</th>
                      <th className="text-center py-3 px-4 font-medium text-white">CTR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {/* Total del Negocio */}
                    <tr className="hover:bg-white/10 transition-colors">
                      <td className="py-4 px-4 font-medium text-white">
                        {auth?.user?.business_name || 'Mi Negocio'}
                      </td>
                      <td className="py-4 px-4 text-center font-semibold text-white">
                        <div>
                          {formatCurrency(totalInvestment, displayCurrency)}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalInvestment, comparisonData?.combined?.investment || comparisonData?.combined?.cost))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalInvestment, comparisonData?.combined?.investment || comparisonData?.combined?.cost))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalConversions.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalConversions, comparisonData?.combined?.conversions))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalConversions, comparisonData?.combined?.conversions))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedCPA > 0 ? 
                            formatCurrency(calculatedCPA, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedCPA > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCPA, comparisonData?.combined?.conversions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.conversions : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedCPA, comparisonData?.combined?.conversions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.conversions : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalClicks.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalClicks, comparisonData?.combined?.clicks))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalClicks, comparisonData?.combined?.clicks))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedCPC > 0 ? 
                            formatCurrency(calculatedCPC, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedCPC > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCPC, comparisonData?.combined?.clicks > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.clicks : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedCPC, comparisonData?.combined?.clicks > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / comparisonData?.combined?.clicks : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalImpressions.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalImpressions, comparisonData?.combined?.impressions))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalImpressions, comparisonData?.combined?.impressions))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedCPM > 0 ? 
                            formatCurrency(calculatedCPM, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedCPM > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCPM, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / (comparisonData?.combined?.impressions / 1000) : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedCPM, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.investment || comparisonData?.combined?.cost) / (comparisonData?.combined?.impressions / 1000) : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedCTR > 0 ? 
                            `${calculatedCTR}%` : 
                            'N/A'
                          }
                          {comparisonData && calculatedCTR > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedCTR, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.clicks / comparisonData?.combined?.impressions) * 100 : 0), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedCTR, comparisonData?.combined?.impressions > 0 ? (comparisonData?.combined?.clicks / comparisonData?.combined?.impressions) * 100 : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Google Ads Row */}
                    <tr className="hover:bg-white/10 transition-colors">
                      <td className="py-4 px-4 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <GoogleAdsLogo />
                          {auth?.user?.google_ads_customer_id && (
                            <span className="text-xs text-white/70">({auth.user.google_ads_customer_id})</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center font-semibold text-white">
                        <div>
                          {formatCurrency(totalGoogleInvestment, displayCurrency)}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalGoogleInvestment, comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalGoogleInvestment, comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalGoogleConversions.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalGoogleConversions, comparisonData?.google_normalized?.conversions))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalGoogleConversions, comparisonData?.google_normalized?.conversions))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedGoogleCPA > 0 ? 
                            formatCurrency(calculatedGoogleCPA, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedGoogleCPA > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedGoogleCPA, comparisonData?.google_normalized?.conversions > 0 ? (comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost) / comparisonData?.google_normalized?.conversions : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedGoogleCPA, comparisonData?.google_normalized?.conversions > 0 ? (comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost) / comparisonData?.google_normalized?.conversions : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalGoogleClicks.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalGoogleClicks, comparisonData?.google_normalized?.clicks))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalGoogleClicks, comparisonData?.google_normalized?.clicks))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedGoogleCPC > 0 ? 
                            formatCurrency(calculatedGoogleCPC, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedGoogleCPC > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedGoogleCPC, comparisonData?.google_normalized?.clicks > 0 ? (comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost) / comparisonData?.google_normalized?.clicks : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedGoogleCPC, comparisonData?.google_normalized?.clicks > 0 ? (comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost) / comparisonData?.google_normalized?.clicks : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalGoogleImpressions.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalGoogleImpressions, comparisonData?.google_normalized?.impressions))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalGoogleImpressions, comparisonData?.google_normalized?.impressions))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedGoogleCPM > 0 ? 
                            formatCurrency(calculatedGoogleCPM, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedGoogleCPM > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedGoogleCPM, comparisonData?.google_normalized?.impressions > 0 ? (comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost) / (comparisonData?.google_normalized?.impressions / 1000) : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedGoogleCPM, comparisonData?.google_normalized?.impressions > 0 ? (comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost) / (comparisonData?.google_normalized?.impressions / 1000) : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedGoogleCTR > 0 ? 
                            `${calculatedGoogleCTR}%` : 
                            'N/A'
                          }
                          {comparisonData && calculatedGoogleCTR > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedGoogleCTR, comparisonData?.google_normalized?.impressions > 0 ? (comparisonData?.google_normalized?.clicks / comparisonData?.google_normalized?.impressions) * 100 : 0), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedGoogleCTR, comparisonData?.google_normalized?.impressions > 0 ? (comparisonData?.google_normalized?.clicks / comparisonData?.google_normalized?.impressions) * 100 : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Meta Ads Row */}
                    <tr className="hover:bg-white/10 transition-colors">
                      <td className="py-4 px-4 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <MetaAdsLogo />
                          {auth?.user?.meta_ad_account_id && (
                            <span className="text-xs text-white/70">({auth.user.meta_ad_account_id})</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center font-semibold text-white">
                        <div>
                          {formatCurrency(totalMetaInvestment, displayCurrency)}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalMetaInvestment, comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalMetaInvestment, comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalMetaConversions.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalMetaConversions, comparisonData?.meta_normalized?.conversions))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalMetaConversions, comparisonData?.meta_normalized?.conversions))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedMetaCPA > 0 ? 
                            formatCurrency(calculatedMetaCPA, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedMetaCPA > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedMetaCPA, comparisonData?.meta_normalized?.conversions > 0 ? (comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost) / comparisonData?.meta_normalized?.conversions : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedMetaCPA, comparisonData?.meta_normalized?.conversions > 0 ? (comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost) / comparisonData?.meta_normalized?.conversions : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalMetaClicks.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalMetaClicks, comparisonData?.meta_normalized?.clicks))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalMetaClicks, comparisonData?.meta_normalized?.clicks))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedMetaCPC > 0 ? 
                            formatCurrency(calculatedMetaCPC, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedMetaCPC > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedMetaCPC, comparisonData?.meta_normalized?.clicks > 0 ? (comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost) / comparisonData?.meta_normalized?.clicks : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedMetaCPC, comparisonData?.meta_normalized?.clicks > 0 ? (comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost) / comparisonData?.meta_normalized?.clicks : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {totalMetaImpressions.toLocaleString()}
                          {comparisonData && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalMetaImpressions, comparisonData?.meta_normalized?.impressions))}`}>
                              {formatPercentageChange(calculatePercentageChange(totalMetaImpressions, comparisonData?.meta_normalized?.impressions))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedMetaCPM > 0 ? 
                            formatCurrency(calculatedMetaCPM, displayCurrency) : 
                            'N/A'
                          }
                          {comparisonData && calculatedMetaCPM > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedMetaCPM, comparisonData?.meta_normalized?.impressions > 0 ? (comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost) / (comparisonData?.meta_normalized?.impressions / 1000) : 0), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedMetaCPM, comparisonData?.meta_normalized?.impressions > 0 ? (comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost) / (comparisonData?.meta_normalized?.impressions / 1000) : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-white/90">
                        <div>
                          {calculatedMetaCTR > 0 ? 
                            `${calculatedMetaCTR}%` : 
                            'N/A'
                          }
                          {comparisonData && calculatedMetaCTR > 0 && (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(calculatedMetaCTR, comparisonData?.meta_normalized?.impressions > 0 ? (comparisonData?.meta_normalized?.clicks / comparisonData?.meta_normalized?.impressions) * 100 : 0), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(calculatedMetaCTR, comparisonData?.meta_normalized?.impressions > 0 ? (comparisonData?.meta_normalized?.clicks / comparisonData?.meta_normalized?.impressions) * 100 : 0))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Leyenda de comparación */}
              {comparisonData && getComparisonDates() && (
                <div className="mt-4">
                  <div className="text-sm text-gray-600 text-center">
                    <span>📈 Comparando contra el período anterior: {getComparisonDates().start} - {getComparisonDates().end}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Modal para fechas personalizadas */}
      {isCustomDateModalOpen && (
        <div className="fixed inset-0 modal-safe-area bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="custom-date-modal backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-6 shadow-lg max-w-md w-full">
            <div className="relative z-10">
              <h3 className="text-lg font-semibold text-white mb-4">Seleccionar Período Personalizado</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Fecha de Inicio
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      setCustomStartDate(e.target.value);
                      setDateValidationError('');
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-md"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Fecha de Fin
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      setCustomEndDate(e.target.value);
                      setDateValidationError('');
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-md"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
                
                {dateValidationError && (
                  <div className="text-red-300 text-sm backdrop-blur-xl bg-red-500/20 border border-red-400/30 rounded-lg p-3 shadow-lg">
                    {dateValidationError}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setIsCustomDateModalOpen(false);
                    setDateValidationError('');
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }}
                  className="flex-1 px-4 py-2 text-white/90 bg-white/10 border border-white/30 rounded-lg hover:bg-white/20 hover:border-white/40 transition-all duration-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmCustomDates}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReporteSemanal;
